import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Text,
  Stack,
  Paper,
  Group,
  Badge,
  Select,
  Table,
  Loader,
  Center,
  ThemeIcon,
  SimpleGrid,
  Progress,
  Button,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconDownload,
  IconSend,
  IconPackageImport,
  IconChecklist,
  IconClock,
  IconAlertTriangle,
  IconFileSpreadsheet,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { getLabTodayDateOnly } from "../../utils/dateOnly";
import {
  useQaMetricsSummary,
  useQaMetricsTrends,
  useQaMetricsByPathologist,
  useQaMetricsByInstitution,
} from "../../hooks/useWorkflows";
import { usePathologists } from "../../hooks/useReferenceData";
import { useExternalInstitutions } from "../../hooks/useSendOuts";
import {
  qaMetricsService,
  QaMetricsFilters,
} from "../../services/workflows.service";
import {
  ReportDateRangePicker,
  getLastMonthRange,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";

const AGREEMENT_COLORS: Record<string, string> = {
  complete: "#40c057",
  minorVariation: "#74b816",
  partial: "#fab005",
  discrepancy: "#fd7e14",
  majorDiscrepancy: "#fa5252",
};

const AGREEMENT_LABELS: Record<string, string> = {
  complete: "Complete Agreement",
  minorVariation: "Minor Variation",
  partial: "Partial Agreement",
  discrepancy: "Discrepancy",
  majorDiscrepancy: "Major Discrepancy",
};

export function QaMetricsContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getLastMonthRange()
  );
  const [pathologistId, setPathologistId] = useState<string | null>(null);
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const filters: QaMetricsFilters = useMemo(
    () => ({
      fromDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
      toDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
      pathologistId: pathologistId || undefined,
      institutionId: institutionId || undefined,
    }),
    [dateRange, pathologistId, institutionId]
  );

  const { data: summary, isLoading: summaryLoading } =
    useQaMetricsSummary(filters);
  const { data: trends, isLoading: trendsLoading } =
    useQaMetricsTrends(filters);
  const { data: byPathologist, isLoading: byPathologistLoading } =
    useQaMetricsByPathologist(filters);
  const { data: byInstitution, isLoading: byInstitutionLoading } =
    useQaMetricsByInstitution(filters);

  // Dropdown options
  const { data: pathologistsRaw } = usePathologists();
  const { data: institutionsRaw } = useExternalInstitutions();

  const pathologistOptions = useMemo(
    () =>
      (pathologistsRaw || []).map((p) => ({
        value: p.id,
        label: p.name || "Unknown",
      })),
    [pathologistsRaw]
  );

  const institutionOptions = useMemo(
    () =>
      (institutionsRaw || []).map((i) => ({
        value: i.id,
        label: i.name || "Unknown",
      })),
    [institutionsRaw]
  );

  // Export handler
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const rows = await qaMetricsService.exportData(filters);
      if (rows.length === 0) {
        setExporting(false);
        return;
      }

      const headers = [
        "Case Number",
        "Patient Name",
        "Original Pathologist",
        "QA Reviewer",
        "Institution",
        "Agreement Level",
        "Recommended Action",
        "Comments",
        "Created At",
        "Completed At",
      ];

      const csvContent = [
        headers.join(","),
        ...rows.map((row) =>
          [
            row.caseNumber,
            `"${(row.patientName || "").replace(/"/g, '""')}"`,
            `"${(row.originalPathologist || "").replace(/"/g, '""')}"`,
            `"${(row.qaReviewer || "").replace(/"/g, '""')}"`,
            `"${(row.institution || "").replace(/"/g, '""')}"`,
            row.agreementLevel,
            row.recommendedAction,
            `"${(row.comments || "").replace(/"/g, '""')}"`,
            row.createdAt ? getLabTodayDateOnly(new Date(row.createdAt)) : "",
            row.completedAt
              ? getLabTodayDateOnly(new Date(row.completedAt))
              : "",
          ].join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qa-metrics-${labToday}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [filters, labToday]);

  // Agreement rate calculation
  const agreementRate = useMemo(() => {
    if (!summary || summary.qaReviewsCompleted === 0) return 0;
    const { complete, minorVariation } = summary.agreementBreakdown;
    return parseFloat(
      (
        ((complete + minorVariation) / summary.qaReviewsCompleted) *
        100
      ).toFixed(1)
    );
  }, [summary]);

  // Chart data transformation for trends
  const chartData = useMemo(() => {
    if (!trends) return [];
    return trends.map((t) => ({
      month: dayjs(`${t.month}-01`).format("MMM 'YY"),
      "Complete Agreement": t.complete,
      "Minor Variation": t.minorVariation,
      "Partial Agreement": t.partial,
      Discrepancy: t.discrepancy,
      "Major Discrepancy": t.majorDiscrepancy,
    }));
  }, [trends]);

  const isLoading =
    summaryLoading ||
    trendsLoading ||
    byPathologistLoading ||
    byInstitutionLoading;

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Select
              label="QA Reviewer"
              placeholder="All reviewers"
              data={pathologistOptions}
              value={pathologistId}
              onChange={setPathologistId}
              clearable
              searchable
              w={220}
            />
            <Select
              label="Institution"
              placeholder="All institutions"
              data={institutionOptions}
              value={institutionId}
              onChange={setInstitutionId}
              clearable
              searchable
              w={220}
            />
            <ReportDateRangePicker value={dateRange} onChange={setDateRange} />
          </Group>
          <Button
            leftSection={<IconDownload size="1rem" />}
            variant="light"
            loading={exporting}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Group>
      </Paper>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : (
        <>
          {/* Summary Cards */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="blue">
                  <IconSend size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Cases Sent Out
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.totalSentOut ?? 0}
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="teal">
                  <IconPackageImport size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Cases Returned
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.totalReturned ?? 0}
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="green">
                  <IconChecklist size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  QA Reviews Completed
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.qaReviewsCompleted ?? 0}
              </Text>
              {(summary?.qaReviewsPending ?? 0) > 0 && (
                <Text size="xs" c="orange">
                  {summary?.qaReviewsPending} pending
                </Text>
              )}
            </Paper>
            <Paper p="md" withBorder>
              <Group gap="xs" mb="xs">
                <ThemeIcon size="sm" variant="light" color="cyan">
                  <IconClock size="0.9rem" />
                </ThemeIcon>
                <Text size="sm" c="dimmed">
                  Avg Turnaround
                </Text>
              </Group>
              <Text size="xl" fw={700}>
                {summary?.averageTurnaroundDays ?? 0} days
              </Text>
            </Paper>
          </SimpleGrid>

          {/* Agreement Rate + Corrected Report Rate */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            {/* Agreement Rate Breakdown */}
            <Paper p="lg" withBorder>
              <Group justify="space-between" mb="md">
                <Text fw={600}>Agreement Rate</Text>
                <Badge
                  size="lg"
                  variant="light"
                  color={
                    agreementRate >= 90
                      ? "green"
                      : agreementRate >= 70
                      ? "yellow"
                      : "red"
                  }
                >
                  {agreementRate}%
                </Badge>
              </Group>
              <Stack gap="sm">
                {Object.entries(AGREEMENT_LABELS).map(([key, label]) => {
                  const count =
                    summary?.agreementBreakdown[
                      key as keyof typeof summary.agreementBreakdown
                    ] ?? 0;
                  const total = summary?.qaReviewsCompleted ?? 0;
                  const pct =
                    total > 0 ? ((count / total) * 100).toFixed(0) : "0";
                  return (
                    <div key={key}>
                      <Group justify="space-between" mb={4}>
                        <Text size="sm">{label}</Text>
                        <Text size="sm" fw={500}>
                          {count} ({pct}%)
                        </Text>
                      </Group>
                      <Progress
                        value={total > 0 ? (count / total) * 100 : 0}
                        color={AGREEMENT_COLORS[key]}
                        size="md"
                      />
                    </div>
                  );
                })}
              </Stack>
            </Paper>

            {/* Corrected Report + Action Breakdown */}
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                Actions & Corrected Report Rate
              </Text>
              <Paper
                p="md"
                mb="md"
                withBorder
                bg={
                  (summary?.correctedReportRate ?? 0) > 5 ? "red.0" : "green.0"
                }
              >
                <Group gap="xs">
                  <ThemeIcon
                    size="sm"
                    variant="light"
                    color={
                      (summary?.correctedReportRate ?? 0) > 5 ? "red" : "green"
                    }
                  >
                    <IconAlertTriangle size="0.9rem" />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>
                    Corrected Report Rate
                  </Text>
                </Group>
                <Text size="xl" fw={700} mt="xs">
                  {summary?.correctedReportRate ?? 0}%
                </Text>
                <Text size="xs" c="dimmed">
                  {summary?.actionBreakdown.amendmentNeeded ?? 0} amendment(s)
                  out of {summary?.qaReviewsCompleted ?? 0} reviews
                </Text>
              </Paper>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm">No Action Needed</Text>
                  <Badge variant="light" color="green">
                    {summary?.actionBreakdown.none ?? 0}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Notified Pathologist</Text>
                  <Badge variant="light" color="blue">
                    {summary?.actionBreakdown.notifyPathologist ?? 0}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Internal Discussion Created</Text>
                  <Badge variant="light" color="orange">
                    {summary?.actionBreakdown.internalDiscussion ?? 0}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm">Amendment Needed</Text>
                  <Badge variant="light" color="red">
                    {summary?.actionBreakdown.amendmentNeeded ?? 0}
                  </Badge>
                </Group>
              </Stack>
            </Paper>
          </SimpleGrid>

          {/* Monthly Trends Chart */}
          {chartData.length > 0 && (
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                Monthly Agreement Trends
              </Text>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar
                    dataKey="Complete Agreement"
                    stackId="a"
                    fill={AGREEMENT_COLORS.complete}
                  />
                  <Bar
                    dataKey="Minor Variation"
                    stackId="a"
                    fill={AGREEMENT_COLORS.minorVariation}
                  />
                  <Bar
                    dataKey="Partial Agreement"
                    stackId="a"
                    fill={AGREEMENT_COLORS.partial}
                  />
                  <Bar
                    dataKey="Discrepancy"
                    stackId="a"
                    fill={AGREEMENT_COLORS.discrepancy}
                  />
                  <Bar
                    dataKey="Major Discrepancy"
                    stackId="a"
                    fill={AGREEMENT_COLORS.majorDiscrepancy}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {/* By Pathologist Table */}
          {byPathologist && byPathologist.length > 0 && (
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                By QA Reviewer
              </Text>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Reviewer</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Total</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Tooltip label="Complete Agreement">
                        <span>Complete</span>
                      </Tooltip>
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Tooltip label="Minor Variation">
                        <span>Minor</span>
                      </Tooltip>
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Partial</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Tooltip label="Discrepancy + Major Discrepancy">
                        <span>Discrepancy</span>
                      </Tooltip>
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      Agreement Rate
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {byPathologist.map((row) => (
                    <Table.Tr key={row.pathologistId}>
                      <Table.Td fw={500}>{row.pathologistName}</Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        {row.totalReviews}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="green" size="sm">
                          {row.complete}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="lime" size="sm">
                          {row.minorVariation}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="yellow" size="sm">
                          {row.partial}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="red" size="sm">
                          {row.discrepancy + row.majorDiscrepancy}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge
                          variant="filled"
                          color={
                            row.agreementRate >= 90
                              ? "green"
                              : row.agreementRate >= 70
                              ? "yellow"
                              : "red"
                          }
                          size="sm"
                        >
                          {row.agreementRate}%
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* By Institution Table */}
          {byInstitution && byInstitution.length > 0 && (
            <Paper p="lg" withBorder>
              <Text fw={600} mb="md">
                By Institution
              </Text>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Institution</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Total</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      Complete
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Minor</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>Partial</Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      Discrepancy
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      Agreement Rate
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {byInstitution.map((row) => (
                    <Table.Tr key={row.institutionId}>
                      <Table.Td fw={500}>{row.institutionName}</Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        {row.totalReviews}
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="green" size="sm">
                          {row.complete}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="lime" size="sm">
                          {row.minorVariation}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="yellow" size="sm">
                          {row.partial}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge variant="light" color="red" size="sm">
                          {row.discrepancy + row.majorDiscrepancy}
                        </Badge>
                      </Table.Td>
                      <Table.Td style={{ textAlign: "center" }}>
                        <Badge
                          variant="filled"
                          color={
                            row.agreementRate >= 90
                              ? "green"
                              : row.agreementRate >= 70
                              ? "yellow"
                              : "red"
                          }
                          size="sm"
                        >
                          {row.agreementRate}%
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* Empty state */}
          {summary?.qaReviewsCompleted === 0 && summary?.totalSentOut === 0 && (
            <Center py="xl">
              <Stack align="center" gap="xs">
                <ThemeIcon size="xl" variant="light" color="gray">
                  <IconFileSpreadsheet size="1.5rem" />
                </ThemeIcon>
                <Text fw={500} c="dimmed">
                  No QA review data for this period
                </Text>
                <Text size="sm" c="dimmed">
                  Adjust the date range or filters to see metrics
                </Text>
              </Stack>
            </Center>
          )}
        </>
      )}
    </Stack>
  );
}

export default function QaMetricsPage() {
  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg" p="md">
          <Group>
            <Button
              component={Link}
              to="/reports"
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
            >
              Reports
            </Button>
          </Group>
          <QaMetricsContent />
        </Stack>
      </Box>
    </Container>
  );
}
